import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui';
import { BrowserImage } from '@/components/ui/BrowserImage';
import type { PublicOrgOrganization } from '@/services/publicOrg';

interface OrgAboutSectionProps {
  organization: PublicOrgOrganization;
}

export function OrgAboutSection({ organization }: OrgAboutSectionProps) {
  const photos = organization.galleryUrls ?? [];
  const [index, setIndex] = useState(0);

  if (!organization.description && photos.length === 0) return null;

  const currentPhoto = photos[index];

  return (
    <section aria-labelledby="about-heading" className="space-y-4">
      <h2 id="about-heading" className="text-2xl font-bold text-foreground">
        About
      </h2>

      {photos.length > 0 ? (
        <div className="relative overflow-hidden rounded-xl bg-muted">
          {currentPhoto ? (
            <BrowserImage
              src={currentPhoto}
              alt={`${organization.name} photo ${index + 1}`}
              className="aspect-[16/9] w-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : null}
          {photos.length > 1 ? (
            <>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2"
                aria-label="Previous photo"
                onClick={() => setIndex((i) => (i === 0 ? photos.length - 1 : i - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                aria-label="Next photo"
                onClick={() => setIndex((i) => (i === photos.length - 1 ? 0 : i + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                {photos.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to photo ${i + 1}`}
                    className={`h-2 w-2 rounded-full ${i === index ? 'bg-white' : 'bg-white/50'}`}
                    onClick={() => setIndex(i)}
                  />
                ))}
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {organization.description ? (
        <p className="whitespace-pre-line text-muted-foreground">{organization.description}</p>
      ) : null}
    </section>
  );
}
